import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { Organization, Alert, Site } from "../types";
import {
  getAlerts,
  getAlertCount,
  resolveAlert,
  batchResolveAlerts,
} from "../services/alertService.ts";
import AlertSummary from "../components/AlertSummary.tsx";
import AlertListTable from "../components/AlertListTable.tsx";
import messageService from "../components/Message/message.ts";
import { Listbox } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import OrganizationSiteFilter from "../components/OrganizationSiteFilter.tsx";

const statusOptions = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "resolved", label: "Resolved" },
];

const DeviceHealthAlert: React.FC = () => {
  const { organizations } = useOutletContext<{
    organizations: Organization[];
  }>();
  const [searchParams] = useSearchParams();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertCount, setAlertCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "resolved"
  >("all");

  // Updated filter states
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [isAllSitesSelected, setIsAllSitesSelected] = useState(false);

  // Handle URL parameters
  useEffect(() => {
    const siteId = searchParams.get("siteId");
    if (siteId && organizations.length > 0) {
      // Find the site and organization from the siteId
      for (const org of organizations) {
        const findSite = (sites: Site[]): Site | null => {
          for (const site of sites) {
            if (site.id === siteId) {
              return site;
            }
            if (site.children) {
              const found = findSite(site.children);
              if (found) return found;
            }
          }
          return null;
        };

        const site = findSite(org.sites);
        if (site) {
          setSelectedOrganization(org);
          setSelectedSite(site);
          setIsAllSitesSelected(false);
          break;
        }
      }
    }
  }, [searchParams, organizations]);

  // 加载告警数据的函数
  const loadAllAlerts = useCallback(async () => {
    setLoading(true);

    let allAlerts: Alert[] = [];
    let totalCount = 0;

    for (const org of organizations) {
      try {
        const countResponse = await getAlertCount(org.id);
        totalCount += countResponse.count;

        const params: any = {
          orgId: org.id,
          limit: "50",
          offset: "0",
        };
        if (statusFilter !== "all") params.status = statusFilter;

        const alertsResponse = await getAlerts(params);
        allAlerts = allAlerts.concat(alertsResponse.alerts);
      } catch (e) {
        console.error(`Failed to load alerts for org ${org.id}:`, e);
      }
    }

    setAlerts(allAlerts);
    setAlertCount(totalCount);
    setLoading(false);
  }, [organizations, statusFilter]);

  // 初始加载
  useEffect(() => {
    loadAllAlerts();
  }, [organizations, statusFilter, loadAllAlerts]);

  // Updated filtering logic
  const filteredAlerts = useMemo(() => {
    return alerts.filter((alert) => {
      // Organization filter
      if (
        selectedOrganization &&
        alert.organization?.id !== selectedOrganization.id
      ) {
        return false;
      }

      // Site filter
      if (
        !isAllSitesSelected &&
        selectedSite &&
        alert.site?.id !== selectedSite.id
      ) {
        return false;
      }

      return true;
    });
  }, [alerts, selectedOrganization, selectedSite, isAllSitesSelected]);

  const handleOrganizationSelect = (org: Organization | null) => {
    setSelectedOrganization(org);
    setSelectedSite(null);
    setIsAllSitesSelected(false);
  };

  const handleSiteSelect = (site: Site | null) => {
    setSelectedSite(site);
    setIsAllSitesSelected(false);
  };

  const handleAllSitesSelect = () => {
    setSelectedSite(null);
    setIsAllSitesSelected(true);
  };

  // 处理告警解决
  const handleResolveAlert = async (alertId: string) => {
    try {
      await resolveAlert(alertId);

      const alert = alerts.find((a) => a.id === alertId);
      const orgId = alert?.organization?.id;
      if (!orgId) {
        console.error("No organization id found for this alert");
        return;
      }

      // 重新加载该 org 的告警
      const params: any = {
        orgId,
        limit: "50",
        offset: "0",
      };
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const alertsResponse = await getAlerts(params);
      setAlerts(alertsResponse.alerts);

      // 更新告警数量
      const countResponse = await getAlertCount(orgId);
      setAlertCount(countResponse.count);
    } catch (error) {
      console.error("Failed to resolve alert:", error);
    }
  };

  // 处理批量解决告警
  const handleBatchResolveAlerts = async (alertIds: string[]) => {
    try {
      const response = await batchResolveAlerts({ alertIds });

      console.log("Batch resolve response:", response);

      // 重新加载所有告警数据
      await loadAllAlerts();

      // 显示成功消息
      const successCount = response.summary.resolved;
      const failedCount = response.summary.failed;

      if (successCount > 0) {
        messageService.success(
          `Successfully resolved ${successCount} alerts${
            failedCount > 0 ? `, ${failedCount} failed` : ""
          }`
        );
      } else if (failedCount > 0) {
        messageService.error(`Failed to resolve ${failedCount} alerts`);
      }
    } catch (error) {
      console.error("Failed to batch resolve alerts:", error);
      messageService.error("Failed to batch resolve alerts. Please try again.");
    }
  };

  // Site 选项（可根据 org 过滤）
  // Removed unused orgOptions and siteOptions

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading alerts...</p>
        </div>
      </div>
    );
  }

  // 如果没有组织，显示提示信息
  if (organizations.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg
              className="mx-auto h-12 w-12"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Organization Available
          </h3>
          <p className="text-gray-500">
            Please select an organization to view alerts.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alert Summary Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <AlertSummary
          alertCount={alertCount}
          activeAlerts={
            filteredAlerts.filter((alert) => alert.status === "active").length
          }
          resolvedAlerts={
            filteredAlerts.filter((alert) => alert.status === "resolved").length
          }
        />
      </div>

      {/* Alert List Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-wrap gap-3 mt-4 mb-2 pl-6">
          {/* Status Filter */}
          <div className="flex items-center bg-gray-100 rounded-full px-2 py-0.5 min-h-[28px] relative w-40">
            <Listbox value={statusFilter} onChange={setStatusFilter}>
              <div className="relative w-full">
                <Listbox.Button className="w-full bg-gray-100 rounded-full px-2 py-0.5 min-h-[28px] text-sm text-left flex items-center justify-between whitespace-nowrap">
                  <span>
                    Status:{" "}
                    {statusOptions.find((o) => o.value === statusFilter)?.label}
                  </span>
                  <ChevronDownIcon className="w-4 h-4 text-gray-500 ml-2" />
                </Listbox.Button>
                <Listbox.Options className="absolute mt-1 w-full bg-white shadow-lg rounded-lg z-10 py-1 text-sm">
                  {statusOptions.map((option) => (
                    <Listbox.Option
                      key={option.value}
                      value={option.value}
                      className={({ active }) =>
                        `cursor-pointer select-none px-4 py-2 ${
                          active ? "bg-gray-100" : ""
                        }`
                      }
                    >
                      {option.label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </div>
            </Listbox>
          </div>
        </div>

        {/* Organization and Site Filter - Replace old filters */}
        <div className="px-6 mb-4">
          <OrganizationSiteFilter
            organizations={organizations}
            selectedOrganization={selectedOrganization}
            selectedSite={selectedSite}
            onOrganizationSelect={handleOrganizationSelect}
            onSiteSelect={handleSiteSelect}
            showAllSitesOption={true}
            isAllSitesSelected={isAllSitesSelected}
            onAllSitesSelect={handleAllSitesSelect}
          />
        </div>

        <AlertListTable
          alerts={filteredAlerts}
          onResolveAlert={handleResolveAlert}
          onBatchResolveAlerts={handleBatchResolveAlerts}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default DeviceHealthAlert;
